import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import {
  LayoutDashboard, FolderKanban, Wallet,
  Package, Wrench, ShoppingCart, UserSquare2, FolderTree, Lightbulb,
  ListTodo, Network, LogOut, Calculator, Gauge
} from "lucide-react";

export const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/projets", label: "Projets", icon: FolderKanban, testid: "nav-projets" },
  { to: "/pilotage", label: "Pilotage", icon: Gauge, testid: "nav-pilotage" },
  { to: "/estimatif", label: "Estimatif", icon: Calculator, testid: "nav-estimatif" },
  { to: "/finance", label: "Finance", icon: Wallet, testid: "nav-finance" },
  { to: "/clients", label: "Clients & Partenaires", icon: UserSquare2, testid: "nav-clients" },
  { to: "/fournisseurs", label: "Fournisseurs", icon: Package, testid: "nav-fournisseurs" },
  { to: "/prestataires", label: "Prestataires", icon: Wrench, testid: "nav-prestataires" },
  { to: "/franchimob", label: "Franchi'Mob", icon: ShoppingCart, testid: "nav-fmob" },
  { to: "/franchiseurs", label: "Franchiseurs", icon: Network, testid: "nav-franchiseurs" },
  { to: "/drive", label: "Drive", icon: FolderTree, testid: "nav-drive" },
  { to: "/ideas", label: "Idées LinkedIn", icon: Lightbulb, testid: "nav-ideas" },
  { to: "/assistant", label: "Vue assistante", icon: ListTodo, testid: "nav-assistant" },
];

export default function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  const { brand, setBrand } = useBrand();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-[var(--border)] bg-white h-screen sticky top-0">
      <BrandHeader/>
      <BrandToggle brand={brand} setBrand={setBrand}/>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={n.testid}
            onClick={() => onNavigate?.()}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          >
            <n.icon size={16} strokeWidth={1.8} />
            <span>{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <UserFooter user={user} onLogout={onLogout}/>
    </aside>
  );
}

export function BrandHeader() {
  return (
    <div className="px-6 py-5 border-b border-[var(--border)]" data-testid="sidebar-brand">
      <div className="font-display text-lg font-bold tracking-tight text-stone-900" data-testid="sidebar-brand-title">
        Pourquoi Pas Design
      </div>
      <div className="text-[10px] text-stone-500 font-medium uppercase tracking-[0.18em] mt-1">
        Architecture · Franchise · Déploiement
      </div>
    </div>
  );
}

export function BrandToggle({ brand, setBrand }) {
  return (
    <div className="px-4 py-3 border-b border-[var(--border)]">
      <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold mb-1.5">Marque active</div>
      <div className="flex gap-1.5">
        {["ALL", "PPD", "FRANCHIMOB"].map((b) => (
          <button
            key={b}
            data-testid={`brand-toggle-${b.toLowerCase()}`}
            onClick={() => setBrand(b)}
            className={`flex-1 text-[11px] font-semibold py-1.5 rounded transition ${
              brand === b
                ? b === "PPD" ? "bg-[var(--ppd)] text-white"
                  : b === "FRANCHIMOB" ? "bg-[var(--fmob)] text-white"
                  : "bg-stone-900 text-white"
                : "bg-stone-50 text-stone-600 hover:bg-stone-100"
            }`}
          >
            {b === "ALL" ? "Toutes" : b === "PPD" ? "PPD" : "Franchi'Mob"}
          </button>
        ))}
      </div>
    </div>
  );
}

export function UserFooter({ user, onLogout }) {
  return (
    <div className="border-t border-[var(--border)] p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-stone-900 text-white flex items-center justify-center text-sm font-semibold">
          {(user?.name || "?")[0]}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-stone-500 truncate">{user?.role}</div>
        </div>
      </div>
      <button
        data-testid="logout-button"
        onClick={onLogout}
        className="w-full flex items-center gap-2 text-xs font-medium text-stone-600 hover:text-stone-900 transition"
      >
        <LogOut size={14} /> Déconnexion
      </button>
    </div>
  );
}
